type Primitive = string | number | boolean | null | undefined | bigint | symbol

export const isObject = (x: unknown): x is object =>
  x !== null && (typeof x === 'object' || typeof x === 'function')

function isRecord(object: unknown): object is Record<keyof never, unknown> {
  return object instanceof Object && object?.constructor === Object
}

export const sortByOrder = <T extends { order: number }>(arr?: T[]): T[] | undefined => {
  return arr === undefined ? undefined : [...arr].sort((a, b) => a.order - b.order)
}

// code from
// https://stackoverflow.com/questions/76000941/how-do-you-add-array-method-polyfill-type-information-to-array-prototype
declare global {
  interface Array<T> {
    group<K extends PropertyKey>(
      callbackfn: (value: T, index: number, array: T[]) => K,
      thisArg?: any
    ): { [P in K]?: T[] }

    groupToMap<K>(
      callbackfn: (value: T, index: number, array: T[]) => K,
      thisArg?: any
    ): Map<K, T[]>
  }
}

// Can't import core-js in the playground so here are my own polyfills, not sure if there are problems with them
// https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Object/groupBy#%E3%83%96%E3%83%A9%E3%82%A6%E3%82%B6%E3%83%BC%E3%81%AE%E4%BA%92%E6%8F%9B%E6%80%A7
Array.prototype.group = function (callbackfn, thisArg) {
  const ret: any = {}
  const fn = thisArg !== undefined ? callbackfn.bind(thisArg) : callbackfn
  this.forEach((v, i, a) => (ret[fn(v, i, a)] ??= []).push(v))
  return ret
}

Array.prototype.groupToMap = function (callbackfn, thisArg) {
  const ret = new Map()
  const fn = thisArg !== undefined ? callbackfn.bind(thisArg) : callbackfn
  this.forEach((v, i, a) => {
    const k = fn(v, i, a)
    if (!ret.has(k)) ret.set(k, [])
    ret.get(k).push(v)
  })
  return ret
}

/**
 * Generates an array of numbers within a specified range.
 *
 * @param {number} start - The starting value of the range (inclusive).
 * @param {number} end - The ending value of the range (exclusive).
 * @param {number} [step=1] - The step size between consecutive values (default is 1).
 *
 * @throws {Error} If step is zero.
 *
 * @returns {number[]} An array of numbers within the specified range.
 *
 * This function generated by chatGPT 3.5
 * Write a Python's "range" function with jsdoc in TypeScript.
 */
export function range(start: number, end: number, step: number = 1): number[] {
  if (step === 0) {
    throw new Error('Step cannot be zero.')
  }

  const result: number[] = []
  if (step > 0) {
    for (let i = start; i < end; i += step) {
      result.push(i)
    }
  } else {
    for (let i = start; i > end; i += step) {
      result.push(i)
    }
  }

  return result
}

export function objectMap<K extends PropertyKey, A, B>(
  object: { [P in K]: A },
  mapper: (value: A) => B
): { [P in K]: B } {
  const result = {} as { [P in K]: B }
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      result[key] = mapper(object[key])
    }
  }
  return result
}

export function pick<T, K extends keyof T>(obj: T, keys: K[]) {
  return keys.reduce(
    (acc, val) => {
      return (acc[val] = obj[val]), acc
    },
    {} as Pick<T, K>
  )
}

export const filterPick = <T extends {}, K extends keyof T & string>(obj: T, ...keys: K[]) =>
  Object.fromEntries(keys.filter((key) => key in obj).map((key) => [key, obj[key]])) as Pick<T, K>

export function omit<T extends object, K extends keyof T>(obj: T, keys: K[]) {
  const omitKeys = Object.keys(obj).filter((key) => !keys.includes(key as K)) as Exclude<
    keyof T,
    K
  >[]
  return omitKeys.reduce(
    (acc, val) => {
      return (acc[val] = obj[val]), acc
    },
    {} as Omit<T, K>
  )
}

type TFunction = (...a: any[]) => any

type TPrimitives = string | number | boolean | bigint | symbol | Date | TFunction

type TArrayToMap<T, Key> =
  T extends Array<infer V extends Record<string, any>>
    ? Map<Key, TArrayToMap<V, Key>>
    : T extends Map<infer K, infer V>
      ? Map<K, TArrayToMap<V, Key>>
      : T extends TPrimitives
        ? T
        : T extends object
          ? { [K in keyof T]: TArrayToMap<T[K], Key> }
          : T

export function arrayToMap<T, K>(xs: T, f: (x: any) => K): TArrayToMap<T, K> {
  if (Array.isArray(xs)) {
    return new Map(
      xs.map((v) => {
        const key = f(v)
        if (key === undefined) throw Error(`key not found in "${Object.keys(v)}"`)
        return [key, arrayToMap(v, f)]
      })
    ) as TArrayToMap<T, K>
  } else if (isObject(xs)) {
    return objectMap(xs as object, (v) => arrayToMap(v, f)) as TArrayToMap<T, K>
  } else {
    return xs as TArrayToMap<T, K>
  }
}

type TMapToArray<T> =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends Map<infer K, infer V>
    ? Array<TMapToArray<V>>
    : T extends TPrimitives
      ? T
      : T extends object
        ? { [K in keyof T]: TMapToArray<T[K]> }
        : T

export function mapToArray<T>(xs: T): TMapToArray<T> {
  if (xs instanceof Map) {
    // console.log('mapToObject is array', xs)
    return [...xs.values()].map((x) => mapToArray(x)) as TMapToArray<T>
  } else if (isRecord(xs)) {
    // console.log('mapToObject is object', xs)
    return objectMap(xs as object, (v) => mapToArray(v)) as TMapToArray<T>
  } else {
    // console.log('mapToObject is other', xs)
    return xs as TMapToArray<T>
  }
}

/**
 * Creates a memoized function that caches the results of the factory function based on the key generated by the key function.
 *
 * @template T - The type of the arguments passed to the key function and factory function.
 * @template V - The type of the key generated by the key function.
 * @template K - The type of the result returned by the factory function.
 * @param {(...args: T) => V} keyFn - A function that generates a key based on the provided arguments.
 * @param {(...args: T) => K} factoryFn - A function that generates the value to be cached based on the provided arguments.
 * @returns {(...args: T) => K} - A memoized function that returns the cached value or computes a new value using the factory function if the key is not found in the cache.
 *
 * @example
 * const keyFn = (x: string) => x;
 * const factoryFn = (x: string) => new Date(Date.parse(x));
 * const memoized = memoizeByKey(keyFn, factoryFn);
 * memoized('2024-01-01').toJSON(); // 2024-01-01T00:00:00.000Z
 * memoized('2024-01-01').toJSON(); // 2024-01-01T00:00:00.000Z (from cache, no parse!)
 * memoized('2024-02-01').toJSON(); // 2024-02-01T00:00:00.000Z
 */
export function memoizeByKey<T extends Array<any>, V, K>(
  keyFn: (...args: T) => V,
  factoryFn: (...args: T) => K
): (...args: T) => K {
  const r: Map<V, K> = new Map()

  /**
   * The memoized function that returns the cached value or computes a new value using the factory function if the key is not found in the cache.
   *
   * @param {...T} args - The arguments to be passed to the key function and factory function.
   * @returns {K} - The cached value or a new value computed by the factory function.
   */
  return function (...args: T): K {
    const key = keyFn(...args)
    if (!r.has(key)) r.set(key, factoryFn(...args))
    return r.get(key)!
  }
}

/**
 * isPartialObject で同値比較可能な要素で構成されることを保証するための型
 */
interface PrimitiveValueObject {
  [key: string]: PrimitiveValue
}
type PrimitiveValue = Primitive | PrimitiveValueObject | PrimitiveValue[]

/**
 * Checks if all key-value pairs in the object `x` are present in the object `y`,
 * considering nested objects and arrays.
 *
 * This function performs a partial deep comparison:
 * - If `x` and `y` are objects, their key-value pairs are recursively compared.
 * - If `x` and `y` are arrays, their elements are recursively compared.
 * - If `x` and `y` are primitive values, strict equality (`===`) is used.
 *
 * @param x - A partial object (`PrimitiveValueObject`) containing key-value pairs to check.
 * @param y - A full object (`PrimitiveValueObject`) to compare against.
 * @returns `true` if all key-value pairs in `x` exist in `y` with matching values, otherwise `false`.
 *
 * @example
 * const partial = { a: 1, b: { c: 2 } };
 * const full = { a: 1, b: { c: 2, d: 3 }, e: 4 };
 * isPartialObject(partial, full); // true
 *
 * const partial2 = { a: 1, b: { c: 5 } };
 * isPartialObject(partial2, full); // false
 */
export function isPartialObject(xs: PrimitiveValueObject, ys: PrimitiveValueObject): boolean {
  const eq = (x: PrimitiveValue, y: PrimitiveValue): boolean => {
    if (isRecord(x) && isRecord(y))
      return isPartialObject(x as PrimitiveValueObject, y as PrimitiveValueObject)
    if (Array.isArray(x) && Array.isArray(y))
      return x.length === y.length && x.every((v, i) => eq(v, y[i]))
    else return x === y
  }
  return Object.entries(xs).every(([k, x]) => eq(x, ys[k]))
}
